import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ToggleLeft, ToggleRight, Wifi, WifiOff } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { integrationService } from '../../services/api';
import { Button, Badge, Skeleton, EmptyState, Toast } from '../../components/ui';
import { thStyle, tdStyle } from '../../utils/tableStyles';

const LeadSyncControl = () => {
  const { dark } = useTheme();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await integrationService.getMyOUSyncMappings();
      setMappings(data.data || []);
    } catch {
      setToast({ msg: 'Failed to load sync mappings', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMappings(); }, [fetchMappings]);

  const handleToggle = async (mapping) => {
    setTogglingId(mapping._id);
    try {
      const { data } = await integrationService.toggleMyOUSyncMapping(mapping._id);
      setMappings(prev => prev.map(m => m._id === mapping._id ? { ...m, isActive: data.data.isActive } : m));
      setToast({ msg: `Sync ${data.data.isActive ? 'enabled' : 'disabled'} for ${mapping.platform} — ${mapping.externalId}`, type: 'success' });
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Toggle failed', type: 'error' });
    } finally {
      setTogglingId(null);
    }
  };

  const card = {
    background: dark ? '#0F172A' : '#fff',
    border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`,
    borderRadius: 16,
    overflow: 'hidden',
  };

  const activeSyncCount = mappings.filter(m => m.isActive).length;

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: 0 }}>
            Lead Sync Control
          </h1>
          <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: '4px 0 0' }}>
            Enable or disable automatic lead sync from social media channels for your Retail OU
          </p>
        </div>
        <Button variant="outline" icon={RefreshCw} onClick={fetchMappings} disabled={loading}>
          Refresh
        </Button>
      </div>

      {/* Summary strip */}
      {!loading && mappings.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <div style={{
            flex: 1, padding: '14px 18px', borderRadius: 12,
            background: dark ? '#0F2A1F' : '#F0FDF4',
            border: `1px solid ${dark ? '#166534' : '#BBF7D0'}`,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Wifi size={18} color="#10B981" />
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#10B981' }}>{activeSyncCount}</div>
              <div style={{ fontSize: 12, color: dark ? '#6EE7B7' : '#065F46' }}>Active Syncs</div>
            </div>
          </div>
          <div style={{
            flex: 1, padding: '14px 18px', borderRadius: 12,
            background: dark ? '#1E1E2E' : '#F8FAFC',
            border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <WifiOff size={18} color="#94A3B8" />
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: dark ? '#CBD5E1' : '#475569' }}>
                {mappings.length - activeSyncCount}
              </div>
              <div style={{ fontSize: 12, color: dark ? '#64748B' : '#94A3B8' }}>Paused Syncs</div>
            </div>
          </div>
          <div style={{
            flex: 1, padding: '14px 18px', borderRadius: 12,
            background: dark ? '#1E293B' : '#F8FAFC',
            border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A' }}>{mappings.length}</div>
            <div style={{ fontSize: 12, color: dark ? '#64748B' : '#94A3B8' }}>Total Channels</div>
          </div>
        </div>
      )}

      <div style={card}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}` }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: dark ? '#94A3B8' : '#64748B', margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Lead Source Channels
          </h2>
        </div>

        <div style={{ padding: 20 }}>
          {loading ? (
            <Skeleton rows={3} cols={4} />
          ) : mappings.length === 0 ? (
            <EmptyState
              title="No sync channels configured"
              description="Ask your admin to configure lead source channel mappings for your Retail OU."
            />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                  <tr>
                    <th style={thStyle(dark)}>Platform</th>
                    <th style={thStyle(dark)}>Channel / Page ID</th>
                    <th style={thStyle(dark)}>Lead Source</th>
                    <th style={thStyle(dark)}>Sync Status</th>
                    <th style={thStyle(dark)}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map(m => (
                    <tr key={m._id} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                      <td style={tdStyle(dark)}>
                        <Badge color={m.platform === 'META' ? '#3B82F6' : '#F59E0B'}>{m.platform}</Badge>
                      </td>
                      <td style={{ ...tdStyle(dark), fontFamily: 'monospace', fontSize: 11 }}>
                        {m.label ? (
                          <span>
                            <span style={{ fontWeight: 600, fontFamily: 'inherit', fontSize: 13 }}>{m.label}</span>
                            <br />
                            <span style={{ color: dark ? '#475569' : '#94A3B8' }}>{m.externalId}</span>
                          </span>
                        ) : m.externalId}
                      </td>
                      <td style={tdStyle(dark)}>
                        {m.leadSource?.name || (
                          <span style={{ color: dark ? '#475569' : '#94A3B8', fontSize: 11 }}>Platform default</span>
                        )}
                      </td>
                      <td style={tdStyle(dark)}>
                        <Badge color={m.isActive ? '#10B981' : '#EF4444'}>
                          {m.isActive ? 'Syncing' : 'Paused'}
                        </Badge>
                      </td>
                      <td style={tdStyle(dark)}>
                        <button
                          onClick={() => handleToggle(m)}
                          disabled={togglingId === m._id}
                          title={m.isActive ? 'Pause sync' : 'Enable sync'}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '6px 14px', borderRadius: 8, border: 'none',
                            cursor: togglingId === m._id ? 'not-allowed' : 'pointer',
                            fontSize: 12, fontWeight: 600,
                            background: m.isActive
                              ? (dark ? '#3F1010' : '#FEF2F2')
                              : (dark ? '#0F2A1F' : '#F0FDF4'),
                            color: m.isActive ? '#EF4444' : '#10B981',
                            opacity: togglingId === m._id ? 0.6 : 1,
                            transition: 'all 0.15s',
                          }}
                        >
                          {m.isActive
                            ? <><ToggleRight size={14} /> Pause</>
                            : <><ToggleLeft size={14} /> Enable</>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div style={{
        marginTop: 16, padding: '12px 16px', borderRadius: 10,
        background: dark ? '#1E3A5F' : '#EFF6FF',
        border: `1px solid ${dark ? '#2563EB' : '#BFDBFE'}`,
        fontSize: 12, color: dark ? '#93C5FD' : '#1D4ED8', lineHeight: 1.6,
      }}>
        <strong>Note:</strong> Pausing a sync stops new leads from being automatically imported from that channel.
        Existing leads are not affected. You can re-enable sync at any time.
      </div>
    </div>
  );
};

export default LeadSyncControl;
